import { Button, Alert, Textarea, Select as DaisySelect } from 'react-daisyui';
import { useState, useMemo, useRef, useEffect, ReactElement } from 'react';
import Select from 'react-select';
import { ToastContainer } from 'react-toastify';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useRollbar } from '@rollbar/react';
import { User } from '@types';
import type { Client, FileItem, JWTFirm, JWTUser } from '@types';
import { useParams } from 'react-router-dom';
import _ from 'lodash';
import { alertErrorMessage, alertMessageSuccess } from '@/utils/alerts';
import { Spinner } from '@/components/Spinner';
import { EmptyState } from '@/components/EmptyState';
import { get, post } from '@/utils/queries';
import { GenericModal } from '@/components/Modal';

interface UserValue {
  value: number;
  label: string;
}

interface Props {
  auth: JWTUser;
  clientId: number;
  isOpen: boolean;
  fileItem?: FileItem;
  filePath?: string;
  onClose: () => void;
}

export default function RequestPermission({
  auth,
  clientId,
  isOpen,
  fileItem,
  filePath,
  onClose,
}: Props) {
  const rollbar = useRollbar();
  const [selectedUsers, setSelectedUsers] = useState<UserValue[]>([]);
  const [selectedFirm, setSelectedFirm] = useState<JWTFirm | undefined>(
    undefined
  );
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [submitAttempted, setSubmitAttempted] = useState<boolean>(false);
  const selectRef = useRef(null);
  const pathParams = useParams();

  const { Option } = DaisySelect;

  const { firms } = auth;
  useEffect(() => {
    if (firms.length === 1) {
      setSelectedFirm(firms[0]);
    }
  }, [firms]);

  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const { data: company, isLoading: isLoadingClient } = get<Client>(
    `account/${clientId}`
  );
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const { data: userData, isValidating: isLoading } = get(
    `users?clientId=${clientId}`
  );
  const path = filePath || pathParams['*'] || '';
  const pathStack = useMemo(() => {
    const params = pathParams['*'] || filePath;
    const parts = _.split(params, '/');
    return _.drop(parts, 2).join('/') || '-';
  }, [pathParams, filePath]);

  const [content, setContent] = useState('');
  useMemo(() => {
    let message = '';
    message = selectedFirm
      ? `${selectedFirm.firm_name} would like to access `
      : `I'd like to access `;
    if (fileItem) {
      message +=
        `${fileItem.name} (${pathStack} folder) through the OpStart Portal. \n` +
        `Please click the link below to review the file and grant access, or email ${
          company?.hubspot_data?.group_email || ''
        }` +
        ` if you need assistance managing user/firm access permissions.`;
    } else {
      message +=
        `the financial files for ${
          !isLoadingClient ? (company?.client_name as string) : ' - '
        }. \n` +
        `Please click the link below to review the portal and grant access, or email ${
          company?.hubspot_data?.group_email || ''
        }` +
        ` if you need assistance managing user/firm access permissions.`;
    }
    setContent(message);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company?.hubspot_data?.group_email, fileItem, pathStack, selectedFirm]);

  const title = fileItem
    ? 'This file has not been shared with you yet'
    : 'No files have been shared with you yet';

  const primaryUser = useMemo(() => {
    if (userData) {
      const primary = (userData as User[]).find((user: User) => user.primary);
      if (primary) {
        setSelectedUsers([{ value: primary.id, label: primary.email_address }]);
      }
      return primary;
    }
    return undefined;
  }, [userData]);

  const options = useMemo(
    () =>
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
      (userData as User[] | undefined)
        ?.filter((user: User) => user.role === 'client')
        .map((user: User) => ({
          value: user.id,
          label: user.email_address,
        })),
    [userData]
  );

  const handleContentChange = (
    event: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    setContent(event.target.value);
  };

  const onSave = async () => {
    if (selectedUsers.length === 0) {
      setSubmitAttempted(true);
    }

    setIsSaving(true);
    let subject = '';
    let redirectUrl = '';
    if (fileItem) {
      const fileOrFolder = fileItem.type === 'folder' ? 'folder' : 'file';
      subject = `${auth.first_name} ${auth.last_name} has requested access to a ${fileOrFolder}`;
      const encodedUri = encodeURIComponent(
        `?third-party=${auth.id}&file-id=${fileItem.id}`
      );
      redirectUrl = `${window.location.origin}/files/${path}${encodedUri}`;
    } else {
      subject = `${auth.first_name} ${auth.last_name} has requested access to financial files`;
      if (selectedFirm) {
        // eslint-disable-next-line max-len
        redirectUrl = `${window.location.origin}/account/${clientId}/f/${selectedFirm.id}?edit-permissions-modal=open&client-user=true`;
      } else {
        // eslint-disable-next-line max-len
        redirectUrl = `${window.location.origin}/account/${clientId}/c/${auth.id}?edit-permissions-modal=open&client-user=true`;
      }
    }
    const type = 'requestPermissions';

    const invalidUserMessage = 'Invalid User';
    let error = false;
    try {
      await Promise.all(
        selectedUsers.map(async (user) => {
          if (!error) {
            // eslint-disable-next-line no-await-in-loop, @typescript-eslint/no-unsafe-assignment
            const res = await post('/files/requestAccess', {
              destination: user.label.toLowerCase(),
              emailSubject: subject,
              linkType: type,
              redirectUrl,
              mainContent: content,
            });
            // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access
            if (res.success) {
              alertMessageSuccess('Link Sent Successfully');
            } else {
              rollbar.error(
                `RequestPermission.tsx - onSave - Error trying to send the magic 
                link for user ${user.label.toLowerCase()}`,
                {
                  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment
                  error: res.error,
                }
              );
              alertErrorMessage('Error sending task reminder');
              error = true;
            }
          }
        })
      );
      setIsSaving(false);
      setSelectedUsers([]);
      onClose();
    } catch (err) {
      if ((err as Error).message.includes('Forbidden'))
        alertErrorMessage(invalidUserMessage);
      setIsSaving(false);
    }
  };

  if (isLoading || isLoadingClient) {
    return <Spinner />;
  }

  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
  if (!userData.length) {
    return <EmptyState name='clients' />;
  }

  return (
    <GenericModal isOpen={isOpen} onClose={onClose} isFullWidth>
      <>
        <ToastContainer />
        <div className='flex h-full flex-col'>
          <div className='flex items-center'>
            <div className='w-full'>
              {firms.length > 1 ? (
                <>
                  <div className='font-bold text-base'>
                    Firm requesting access:
                  </div>
                  <DaisySelect
                    value={selectedFirm ? selectedFirm?.id : 0}
                    className='select select-bordered select-accent w-full mt-4 mb-5'
                    disabled={firms.length <= 1}
                    onChange={(selectedFirmId: number) => {
                      const firm = firms?.find(
                        (f) => f.id === Number(selectedFirmId)
                      ) as JWTFirm;
                      setSelectedFirm(firm);
                    }}
                  >
                    <Option value={0} disabled>
                      Select a firm
                    </Option>
                    {
                      firms?.map((firm) => (
                        <Option
                          key={`firm_${firm.firm_name}`}
                          value={String(firm.id)}
                        >
                          {firm.firm_name}
                        </Option>
                      )) as unknown as ReactElement
                    }
                  </DaisySelect>
                  <br />
                  {/* <br /> */}
                </>
              ) : null}
              {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
              {!primaryUser ? (
                <>
                  <div className='font-bold text-base'>Select Users:</div>
                  <br />
                  <div className='font-bold text-sm text-left pl-3'>
                    <Select
                      id='selectuser'
                      isMulti
                      className='react-select-container border-accent'
                      classNamePrefix='react-select'
                      placeholder='select clients ...'
                      closeMenuOnSelect={false}
                      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                      options={options}
                      value={selectedUsers}
                      ref={selectRef}
                      onChange={(value) =>
                        setSelectedUsers(value as UserValue[])
                      }
                    />
                    {submitAttempted && !selectedUsers?.length && (
                      <Alert
                        icon={<ExclamationTriangleIcon className='w-5 h-5' />}
                        className='my-2 text-sm'
                      >
                        Please select at least one user.
                      </Alert>
                    )}
                  </div>
                  <br />
                </>
              ) : null}
              <div className='font-bold text-base'>
                {/* Edit the message we will send to the user(s): */}
                {title}
              </div>
              <div className='text-sm'>
                Send the company a message (below) to request access
              </div>
              <Textarea
                id='message-founders'
                className='w-full input-accent p-2 rounded-md mt-4 text-left'
                rows={15}
                cols={20}
                placeholder='Content'
                value={content}
                onChange={handleContentChange}
              />
            </div>
          </div>
          <br />
          <br />
          <div className='flex justify-center'>
            <Button
              type='submit'
              title='confirm'
              color='accent'
              size='sm'
              className='btn btn-accent'
              // eslint-disable-next-line @typescript-eslint/no-misused-promises
              onClick={onSave}
              disabled={
                !selectedUsers?.length ||
                (selectedFirm === undefined && firms.length > 0)
              }
            >
              Send
              {isSaving && <Spinner className='mx-auto w-4 text-base ml-4' />}
            </Button>
          </div>
        </div>
      </>
    </GenericModal>
  );
}
